import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { HubConnectionState } from "@microsoft/signalr";
import {
  fetchNotifications, fetchUnreadCount, markNotificationRead, markAllNotificationsRead,
  type AppNotification
} from "@/api/notifications";
import { getNotificationsConnection } from "@/realtime/notificationsHub";

const LIST_KEY = ["notifications"];
const COUNT_KEY = ["notifications-unread-count"];

/** REST for the initial list/count, SignalR for everything after that - no
 * polling. A push just updates the same React Query cache the REST calls
 * populated, so the rest of the UI doesn't need to know which source it
 * came from. */
export function useNotifications() {
  const queryClient = useQueryClient();

  const { data: notifications } = useQuery({ queryKey: LIST_KEY, queryFn: fetchNotifications });
  const { data: unreadCount } = useQuery({ queryKey: COUNT_KEY, queryFn: fetchUnreadCount });

  useEffect(() => {
    const connection = getNotificationsConnection();

    function handleReceive(notification: AppNotification) {
      queryClient.setQueryData<AppNotification[]>(LIST_KEY, (prev) => [notification, ...(prev ?? [])].slice(0, 30));
      queryClient.setQueryData<number>(COUNT_KEY, (prev) => (prev ?? 0) + 1);
    }

    connection.on("ReceiveNotification", handleReceive);
    if (connection.state === HubConnectionState.Disconnected) {
      connection.start().catch(() => {
        // Automatic reconnect (configured on the connection) keeps retrying;
        // a failed first connect isn't fatal, the REST-fetched list still works.
      });
    }

    return () => {
      connection.off("ReceiveNotification", handleReceive);
    };
  }, [queryClient]);

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: (_, id) => {
      queryClient.setQueryData<AppNotification[]>(LIST_KEY, (prev) => prev?.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      queryClient.setQueryData<number>(COUNT_KEY, (prev) => Math.max(0, (prev ?? 1) - 1));
    }
  });

  const markAllRead = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.setQueryData<AppNotification[]>(LIST_KEY, (prev) => prev?.map((n) => ({ ...n, isRead: true })));
      queryClient.setQueryData<number>(COUNT_KEY, 0);
    }
  });

  return {
    notifications: notifications ?? [],
    unreadCount: unreadCount ?? 0,
    markRead: markRead.mutate,
    markAllRead: markAllRead.mutate
  };
}
